import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import styles from './MetricInfoPopover.module.scss';

const MetricInfoPopover = ({
  title,
  description,
  formula,
  source,
  formulaLabel = 'Fórmula',
  label = 'Ver explicación de la métrica',
  hoverable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [position, setPosition] = useState(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const tooltipId = useId();
  const visible = open || (hoverable && hovered);

  const updatePosition = useCallback(() => {
    if (!visible || !triggerRef.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 12;
    const fitsAbove = triggerRect.top >= popoverRect.height + gap + viewportPadding;
    const top = fitsAbove
      ? triggerRect.top - popoverRect.height - gap
      : triggerRect.bottom + gap;
    const centeredLeft = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2);
    const left = Math.min(
      Math.max(viewportPadding, centeredLeft),
      window.innerWidth - popoverRect.width - viewportPadding,
    );

    setPosition({
      top,
      left,
      placement: fitsAbove ? 'top' : 'bottom',
      arrowLeft: Math.min(
        Math.max(16, triggerRect.left + (triggerRect.width / 2) - left),
        popoverRect.width - 16,
      ),
    });
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setPosition(null);
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    const frame = window.requestAnimationFrame(updatePosition);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.cancelAnimationFrame(frame);
    };
  }, [visible, updatePosition]);

  return (
    <span
      ref={wrapperRef}
      className={`${styles.wrapper} ${open ? styles.open : ''} ${hoverable && hovered ? styles.hovered : ''}`}
      onMouseEnter={() => hoverable && setHovered(true)}
      onMouseLeave={() => hoverable && setHovered(false)}
    >
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        aria-label={label}
        aria-controls={tooltipId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-describedby={tooltipId}
        onFocus={() => hoverable && setHovered(true)}
        onBlur={() => hoverable && setHovered(false)}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <Info size={13} strokeWidth={2.2} aria-hidden="true" />
      </button>
      <span
        id={tooltipId}
        ref={popoverRef}
        role="dialog"
        aria-label={title}
        aria-hidden={!visible}
        data-placement={position?.placement || 'top'}
        className={styles.popover}
        style={position ? {
          top: `${position.top}px`,
          left: `${position.left}px`,
          '--popover-arrow-left': `${position.arrowLeft}px`,
        } : undefined}
      >
        <strong>{title}</strong>
        <span className={styles.description}>{description}</span>
        {formula && (
          <span className={styles.formula}>
            <small>{formulaLabel}</small>
            <span>{formula}</span>
          </span>
        )}
        {source && <span className={styles.source}>{source}</span>}
      </span>
    </span>
  );
};

export default MetricInfoPopover;
